// MARK://dhk8/pos/features/training/TrainingBanner
// Slim full-width bar pinned to the top of the content area (under the topbar)
// while training mode is active. States plainly that nothing is saved and offers
// a one-tap exit. Visible on every screen because it's rendered inside the shell
// content wrapper in App.tsx, above the routed page.
//
// Renders nothing when training mode is off, so it's safe to mount
// unconditionally. Themed via --pos-* tokens (dark + light); collapses cleanly
// at 375px (the label truncates, the button keeps its 44pt hit area).

import { GraduationCap } from 'lucide-react'

import { useTrainingModeContext } from '../../lib/training-mode.js'
import { t } from '../../i18n/es-MX.js'

export default function TrainingBanner() {
  const { training, setTrainingMode } = useTrainingModeContext()
  if (!training) return null

  return (
    <div className="pos-training-banner" role="status" aria-live="polite">
      <GraduationCap size={18} aria-hidden className="pos-training-banner__icon" />
      <span className="pos-training-banner__text">{t('training.bannerText')}</span>
      <button
        type="button"
        className="pos-training-banner__exit"
        onClick={() => setTrainingMode(false)}
      >
        {t('training.exit')}
      </button>
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .pos-training-banner {
    display: flex;
    align-items: center;
    gap: var(--pos-space-2);
    width: 100%;
    box-sizing: border-box;
    min-height: 44px;
    padding: var(--pos-space-2) var(--pos-space-3);
    margin-bottom: var(--pos-space-4);
    border-radius: var(--pos-radius-md);
    background: var(--pos-warning);
    color: #2A1B00;
    font-family: var(--font-display, var(--font-sans));
    font-size: var(--pos-text-sm);
    font-weight: var(--pos-weight-semibold);
    letter-spacing: var(--pos-tracking-snug);
    box-shadow: var(--pos-shadow-1);
    user-select: none;
  }
  .pos-training-banner__icon { flex: none; }
  .pos-training-banner__text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .pos-training-banner__exit {
    flex: none;
    appearance: none;
    border: none;
    border-radius: var(--pos-radius-pill);
    min-height: 32px;
    padding: 0 var(--pos-space-3);
    background: rgba(42, 27, 0, 0.16);
    color: #2A1B00;
    font: inherit;
    font-size: var(--pos-text-xs);
    font-weight: var(--pos-weight-semibold);
    cursor: pointer;
    transition: background var(--pos-duration-fast) var(--pos-ease-out);
  }
  .pos-training-banner__exit:hover { background: rgba(42, 27, 0, 0.28); }
  .pos-training-banner__exit:active { background: rgba(42, 27, 0, 0.34); }

  @media (max-width: 520px) {
    .pos-training-banner { font-size: var(--pos-text-xs); }
  }
`
